/**
 * Admin · calibration layer (GET/POST /admin/models). REAL: edits the per-vertical competency
 * weights the scorer actually reads (model_registry active row → metrics.weights). No trained
 * model is claimed — these are human-tuned weights; learned models train on pilot data.
 *
 * SPLIT WITH THE VERTICALS PAGE. Verticals owns the whole definition — competencies, labels,
 * anchors, fallback questions and the DEFAULT weight of each. This page owns exactly one thing:
 * the serving override for a vertical, stored on model_registry and reversible without touching
 * the definition. Two editors for the anchors would be two answers to "what did this score mean".
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ModelRegistryEntry, Vertical } from "@talenthop/shared";
import { normalisedWeights } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { Info, Bolt, Alert } from "../../components/icons.js";
import { apiErrorText } from "./adminUi.js";
import { useAdminVerticals } from "./Verticals.js";

interface ModelsResponse {
  models: ModelRegistryEntry[];
}

type ModelStatus = ModelRegistryEntry["status"];

const STATUS_PILL: Record<ModelStatus, string> = {
  active: "pill v",
  candidate: "pill r",
  retired: "pill n",
};

/** Azure ML workspace name — where learned models will train once pilot labels accrue. Shown as
 *  plain text (no deep link): the portal URL needs the subscription id, which isn't wired in here. */
const AZURE_ML_WORKSPACE = "talenthop-mlw";

/** Read the weights map from a model's metrics (else null). */
function metricsWeights(metrics: Record<string, unknown>): Record<string, number> | null {
  const w = metrics.weights;
  if (w && typeof w === "object") {
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(w as Record<string, unknown>)) {
      if (typeof v === "number") out[k] = v;
    }
    return out;
  }
  return null;
}

/** One registry row's serving weights, bound to its vertical's competencies. */
function WeightsCard({
  model,
  vertical,
}: {
  model: ModelRegistryEntry;
  vertical: Vertical;
}): ReactNode {
  const queryClient = useQueryClient();
  const competencies = vertical.competencies;
  const stored = metricsWeights(model.metrics);

  const initial: Record<string, number> = {};
  for (const c of competencies) initial[c.competencyId] = stored?.[c.competencyId] ?? c.weight;

  const [weights, setWeights] = useState<Record<string, number>>(initial);

  const sum = competencies.reduce((acc, c) => acc + (weights[c.competencyId] ?? 0), 0);
  const dirty = competencies.some(
    (c) => (weights[c.competencyId] ?? 0) !== (initial[c.competencyId] ?? 0),
  );
  // What each competency actually contributes once normalised — the number that decides a score,
  // as opposed to the raw figure being typed.
  const shares = normalisedWeights({
    competencies: competencies.map((c) => ({ ...c, weight: weights[c.competencyId] ?? 0 })),
  });

  const save = useMutation({
    mutationFn: () =>
      api.post<{ model: ModelRegistryEntry }>("/admin/models", {
        vertical: model.vertical,
        name: model.name,
        version: model.version,
        status: model.status,
        metrics: { ...model.metrics, weights },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin-models"] }),
  });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <h3>{model.name}</h3>
        <span className={STATUS_PILL[model.status]} style={{ textTransform: "capitalize" }}>
          {model.status}
        </span>
      </div>
      <div className="card-b">
        <div className="mut sm" style={{ marginBottom: 10 }}>
          {/* The worker only reads the active row per vertical — so only an active row is actually
              "serving". For candidate/retired rows the scorer keeps using the active row's weights,
              and edits here just stage a future config. */}
          {model.status === "active"
            ? stored
              ? "Serving custom weights — read for requisitions scored from now on (in-progress ones keep their rubric)."
              : `Serving the ${vertical.name} vertical's own weights — save to pin an override.`
            : "Not serving — the scorer reads the active row for this vertical. Edits here stage weights for when this row goes active."}{" "}
          Version {model.version}.
        </div>
        {competencies.map((c) => (
          <div
            key={c.competencyId}
            style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 0" }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>{c.label}</div>
              <div className="mut sm">
                vertical default {c.weight} · {Math.round((shares[c.competencyId] ?? 0) * 100)}% of the composite
              </div>
            </div>
            <input
              className="input"
              type="number"
              min={0}
              max={100}
              step={1}
              value={String(weights[c.competencyId] ?? 0)}
              onChange={(e) => {
                const n = Number(e.target.value);
                setWeights((cur) => ({ ...cur, [c.competencyId]: Number.isFinite(n) ? n : 0 }));
              }}
              style={{ width: 88, textAlign: "right" }}
              disabled={save.isPending}
            />
          </div>
        ))}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            marginTop: 10,
            paddingTop: 10,
            borderTop: "1px solid var(--line)",
          }}
        >
          <span className="mut sm">
            Weight sum <b className="tnum">{sum}</b>{" "}
            {sum === 0 ? (
              // An all-zero calibration rubric is meaningless (since 0099 it no longer drives interview scores) — refuse to save it.
              <span className="hint" style={{ color: "var(--crit)" }}>
                — all weights are 0; at least one competency must carry weight before saving.
              </span>
            ) : (
              <span className="hint">(relative — the scorer normalizes; they need not total 100)</span>
            )}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" disabled={!dirty || save.isPending} onClick={() => setWeights(initial)}>
              Reset
            </button>
            <button
              className="btn primary"
              disabled={!dirty || sum === 0 || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Saving…" : "Save weights"}
            </button>
          </div>
        </div>
        {save.isError && (
          <div className="banner crit" style={{ marginTop: 12 }}>
            <Alert />
            <span>{apiErrorText(save.error, "Couldn't save weights. Please retry.")}</span>
          </div>
        )}
        {save.isSuccess && !dirty && (
          <div className="banner info" style={{ marginTop: 12 }}>
            <Info />
            <span>
              {model.status === "active"
                ? "Saved. Applies to requisitions scored from now on; requisitions already in progress keep the rubric version they started with."
                : "Saved. These weights stage for when this row goes active — the scorer still reads the active row until then."}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

interface OurModelResponse {
  card: {
    name: string;
    purpose: string;
    algorithm: string;
    version: string;
    trainedAt: string;
    catalogueFingerprint: string;
    runsWhere: string;
    heldOut: {
      familyTop1: number;
      familyTop3: number;
      industryTop1: number;
      verticalTop1: number;
      coverageAtThreshold: number;
      accuracyAtThreshold: number;
    };
    corpus: { trainJobPosts: number; trainCatalogueRestatements: number; test: number };
    gate: { requiresTextToNameTheFamily: boolean; familyConfidenceFloor: number; explanation: string };
    measuredAgainst: string;
    caveat: string;
    /** Present once an axis has been hand-audited. Optional so an older API keeps rendering. */
    audit?: {
      axis: string;
      auditedAt: string;
      artefactVersion: string;
      stale: boolean;
      sampled: number;
      confirmedRows: number;
      top1AccuracyOnConfirmed: number;
      top1AgreementOnSample: number;
      top1AccuracyWhereTruthRecoverable: number;
      contradictedLabels: number;
      unevidencedLabels: number;
      caveat: string;
    };
  };
  live: {
    hasLiveData: boolean;
    answered: number;
    classifierShare: number | null;
    bySource: Record<
      string,
      { inferences: number; finalised: number; corrected: number; correctionRate: number | null }
    >;
    note: string;
  };
}

const pct = (n: number | null | undefined): string => (n == null ? "—" : `${Math.round(n * 100)}%`);

/**
 * The one screen that answers "is the model we trained actually any good?".
 *
 * Deliberately splits CLAIMED from OBSERVED. The training numbers are agreement with a
 * machine-written label on a machine-written corpus; the live correction rate is a real recruiter
 * changing a real answer. Shown as one undifferentiated block, the weaker evidence would borrow
 * the authority of the stronger — which is how a model card turns into marketing.
 */
function OurModelPanel(): ReactNode {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-our-model"],
    queryFn: () => api.get<OurModelResponse>("/admin/our-model"),
  });
  if (isLoading || isError || !data) return null;
  const { card, live } = data;
  const classifier = live.bySource.classifier;
  const gemini = live.bySource.gemini;

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div
        className="card-h"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
      >
        <h3 style={{ margin: 0 }}>Our own model · role classifier</h3>
        <span className="pill v">Live in production</span>
      </div>
      <div className="pad">
        <div className="mut sm" style={{ marginBottom: 14 }}>
          {card.purpose}. {card.algorithm}, v{card.version} — {card.runsWhere}.
        </div>

        <div className="grid g3" style={{ gap: 12, marginBottom: 14 }}>
          <div className="card pad">
            <div className="mut sm">Answered with no vendor call</div>
            <div className="tnum" style={{ fontSize: 26, fontWeight: 800 }}>
              {pct(card.heldOut.coverageAtThreshold)}
            </div>
            <div className="mut sm">
              of job posts, at {pct(card.heldOut.accuracyAtThreshold)} agreement on those
            </div>
          </div>
          <div className="card pad">
            <div className="mut sm">Role family · top-1 / top-3</div>
            <div className="tnum" style={{ fontSize: 26, fontWeight: 800 }}>
              {pct(card.heldOut.familyTop1)}{" "}
              <span style={{ fontSize: 16, fontWeight: 600, color: "var(--ink-mute)" }}>
                / {pct(card.heldOut.familyTop3)}
              </span>
            </div>
            <div className="mut sm">across the whole held-out split</div>
          </div>
          <div className="card pad">
            <div className="mut sm">Vertical picked correctly</div>
            <div className="tnum" style={{ fontSize: 26, fontWeight: 800 }}>
              {pct(card.heldOut.verticalTop1)}
            </div>
            <div className="mut sm">which is what decides the rubric</div>
          </div>
        </div>

        <div className="mut sm" style={{ marginBottom: 12 }}>
          <b>Why it answers only some of them:</b> {card.gate.explanation}
        </div>

        <div className="banner info" style={{ marginBottom: 14 }}>
          <Info />
          <div style={{ fontSize: 12.5 }}>
            <b>These are agreement rates, not accuracy.</b> {card.caveat}
          </div>
        </div>

        {/*
          THE AUDITED AXIS, ON ITS OWN, BELOW THE CAVEAT AND NEVER INSIDE THE TILES ABOVE.
          Its number is a different measurement from `heldOut.industryTop1` — accuracy over the
          posts a reader confirms, against agreement over everything — and putting the two side by
          side as equals is how the stronger figure ends up quoted for the weaker one. `stale`
          fires when the shipped weights are no longer the ones the audit was run against, in
          which case the figure describes a model that no longer exists and says so.
        */}
        {card.audit ? (
          <div className={`banner ${card.audit.stale ? "warn" : "info"}`} style={{ marginBottom: 14 }}>
            <Info />
            <div style={{ fontSize: 12.5 }}>
              {card.audit.stale ? (
                <>
                  <b>Audit is stale.</b> The {card.audit.axis} audit of {card.audit.auditedAt} was run
                  against artefact v{card.audit.artefactVersion}, which is not the version shipping.
                  Do not quote it.
                </>
              ) : (
                <>
                  <b>
                    Hand-audited: {card.audit.axis} {pct(card.audit.top1AccuracyOnConfirmed)} accuracy
                  </b>{" "}
                  on the {card.audit.confirmedRows} of {card.audit.sampled} sampled posts that state
                  their own industry ({card.audit.auditedAt}), against{" "}
                  {pct(card.audit.top1AgreementOnSample)} agreement over the whole sample. The audit
                  found {card.audit.contradictedLabels} labels that contradict their own post and{" "}
                  {card.audit.unevidencedLabels} that name an industry it never mentions.{" "}
                  {card.audit.caveat}
                </>
              )}
            </div>
          </div>
        ) : null}

        <h4 style={{ margin: "0 0 6px", fontSize: 14 }}>What it is doing on real requisitions</h4>
        {!live.hasLiveData ? (
          <div className="mut sm">{live.note}</div>
        ) : (
          <>
            <div className="mut sm" style={{ marginBottom: 8 }}>
              {live.answered} requirement{live.answered === 1 ? "" : "s"} inferred ·{" "}
              <b>{pct(live.classifierShare)}</b> answered by our model, the rest by Gemini.{" "}
              {live.note}
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Engine</th>
                  <th>Inferred</th>
                  <th>Finalised</th>
                  <th>Recruiter corrected</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <b>Our classifier</b>
                  </td>
                  <td className="tnum">{classifier?.inferences ?? 0}</td>
                  <td className="tnum">{classifier?.finalised ?? 0}</td>
                  <td className="tnum">{pct(classifier?.correctionRate)}</td>
                </tr>
                <tr>
                  <td>Gemini</td>
                  <td className="tnum">{gemini?.inferences ?? 0}</td>
                  <td className="tnum">{gemini?.finalised ?? 0}</td>
                  <td className="tnum">{pct(gemini?.correctionRate)}</td>
                </tr>
              </tbody>
            </table>
            <div className="mut sm" style={{ marginTop: 8 }}>
              Every correction is kept and becomes training data for the next retrain. The loop only
              turns if recruiters keep fixing what they disagree with.
            </div>
          </>
        )}

        <div className="mut sm" style={{ marginTop: 12 }}>
          Trained {new Date(card.trainedAt).toLocaleDateString()} on{" "}
          {card.corpus.trainJobPosts.toLocaleString()} generated job posts plus{" "}
          {card.corpus.trainCatalogueRestatements.toLocaleString()} catalogue restatements, held out
          on {card.corpus.test.toLocaleString()}. Catalogue fingerprint {card.catalogueFingerprint}.
        </div>
      </div>
    </div>
  );
}

export function Models(): ReactNode {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-models"],
    queryFn: () => api.get<ModelsResponse>("/admin/models"),
  });
  const { verticals, isLoading: verticalsLoading } = useAdminVerticals();
  const models = data?.models ?? [];

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const ordered = [...verticals].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
  const selected = ordered.find((v) => v.key === selectedKey) ?? ordered[0];
  const rows = selected ? models.filter((m) => m.vertical === selected.key) : [];

  // Registry rows pointing at a vertical that no longer exists — surfaced rather than hidden,
  // because a row nothing can look up is a calibration an owner believes is serving when it isn't.
  const orphanKeys = [
    ...new Set(
      models.filter((m) => !verticals.some((v) => v.key === m.vertical)).map((m) => m.vertical),
    ),
  ];

  const pinWeights = useMutation({
    // Creates the serving row for a vertical that has none, seeded with the vertical's OWN weights
    // — pinning alone changes no score, and the owner tunes from a known-equivalent baseline.
    mutationFn: (v: Vertical) =>
      api.post<{ model: ModelRegistryEntry }>("/admin/models", {
        vertical: v.key,
        name: `${v.name} rubric`,
        version: "v1",
        status: "active",
        metrics: {
          weights: Object.fromEntries(v.competencies.map((c) => [c.competencyId, c.weight])),
        },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin-models"] }),
  });

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Calibration layer</h1>
          <div className="sub">The per-vertical competency weights the scorer reads today — human-tuned, honest v0</div>
        </div>
        <span className="pill n">Operator view</span>
      </div>

      <OurModelPanel />

      <div className="banner info" style={{ marginBottom: 18 }}>
        <Info />
        <div>
          <b>Calibration layer — human-tuned weights the scorer reads today.</b>{" "}
          These weights set how much each competency counts in the deterministic composite. The LLM judge stays{" "}
          <b>frozen</b>, and the classifier above never touches scoring — it picks the ROLE, not the grade.
          A learned SCORING instrument still waits on consented pilot labels.
        </div>
      </div>

      {/* One editor per fact: the definition lives one click away, the serving weights live here. */}
      <div className="card pad" style={{ marginBottom: 18, display: "flex", alignItems: "center", gap: 12 }}>
        <Bolt />
        <div style={{ flex: 1 }} className="mut sm">
          A competency's label, rubric anchors, fallback questions and <i>default</i> weight belong to
          the vertical itself. This page only overrides what each one <i>counts for</i> when scoring —
          reversibly, and without editing the definition every past score was measured against.
        </div>
        <Link className="btn" to="/admin/verticals">
          Edit verticals
        </Link>
      </div>

      {(isLoading || verticalsLoading) && <div className="card pad mut sm">Loading calibration registry…</div>}

      {isError && (
        <div className="banner crit">
          <Alert />
          <span>Couldn't load the calibration registry. Please retry in a moment.</span>
        </div>
      )}

      {!isLoading && !verticalsLoading && !isError && !selected && (
        <div className="card pad" style={{ marginBottom: 16 }}>
          <p className="mut sm" style={{ margin: 0 }}>
            No verticals exist yet, so there is nothing to calibrate — create one on the{" "}
            <Link to="/admin/verticals">Verticals</Link> page first.
          </p>
        </div>
      )}

      {!isLoading && !verticalsLoading && !isError && selected && (
        <>
          <div className="field" style={{ maxWidth: 420 }}>
            <label htmlFor="cal-vertical">Vertical</label>
            <select
              id="cal-vertical"
              className="input"
              value={selected.key}
              onChange={(e) => setSelectedKey(e.target.value)}
            >
              {ordered.map((v) => (
                <option key={v.key} value={v.key}>
                  {v.name}
                  {v.isActive ? "" : " (inactive)"}
                </option>
              ))}
            </select>
            <div className="hint">
              {selected.competencies.length} competenc{selected.competencies.length === 1 ? "y" : "ies"} ·
              definition at edition v{selected.version}
            </div>
          </div>

          {rows.length === 0 && (
            <div className="card pad" style={{ marginBottom: 16 }}>
              <p className="mut sm" style={{ margin: "0 0 12px" }}>
                No calibration row for <b>{selected.name}</b> — the scorer is using the vertical's own
                weights. Pin an override to tune them here without touching the definition.
              </p>
              <button
                className="btn primary"
                disabled={pinWeights.isPending}
                onClick={() => pinWeights.mutate(selected)}
              >
                {pinWeights.isPending ? "Pinning…" : "Pin custom weights"}
              </button>
              {pinWeights.isError && (
                <div className="banner crit" style={{ marginTop: 12 }}>
                  <Alert />
                  <span>
                    {apiErrorText(pinWeights.error, "Couldn't create the calibration row. Please retry.")}
                  </span>
                </div>
              )}
            </div>
          )}

          {rows.map((m) => (
            <WeightsCard key={m.id} model={m} vertical={selected} />
          ))}
        </>
      )}

      {orphanKeys.length > 0 && (
        <div className="banner warn" style={{ marginBottom: 18 }}>
          <Alert />
          <div>
            <b>
              {orphanKeys.length} calibration row{orphanKeys.length === 1 ? "" : "s"} point at a
              vertical that no longer exists
            </b>{" "}
            ({orphanKeys.join(", ")}). Nothing reads them — the scorer looks weights up by vertical
            key. Recreate the vertical under that key, or leave them; they are inert either way.
          </div>
        </div>
      )}

      <div className="card pad" style={{ marginBottom: 18, display: "flex", alignItems: "center", gap: 10 }}>
        <Bolt />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Azure ML workspace</div>
          <div className="mut sm" style={{ fontFamily: "var(--mono)", fontSize: 12 }}>
            {AZURE_ML_WORKSPACE} · training starts once consented pilot labels accrue
          </div>
        </div>
      </div>

      <div className="disclaimer">
        <b>These are relative weights, not a learned model.</b> The scorer normalizes them and the fairness gate
        (four-fifths adverse-impact ratio) still governs whether any vertical serves scored decisions. Learned
        instruments train later on de-identified, consent-gated pilot labels — never on raw video, face or
        demographic attributes.
      </div>
    </section>
  );
}
